import React, { useCallback, useMemo } from "react";
import { Entity } from "@common/types/entity";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { EntityDisplay } from "@components/entity/EntityDisplay";

interface Props {
    entities: Entity[];
    selectedEntity?: Entity;
    onChange: (entity: Entity | undefined) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
}

export const AccountingEntitySelect: React.FC<Props> = ({
    entities,
    selectedEntity,
    onChange,
    disabled = false,
    placeholder = "Select entity",
    className,
}) => {
    const entitiesById = useMemo(
        () => new Map(entities.map((entity) => [entity.id, entity])),
        [entities],
    );
    const items = useMemo<Array<PickerItemData<number>>>(
        () =>
            entities.map((entity) => ({
                value: entity.id,
                label: entity.name ?? "",
                leading: <EntityAvatar entity={entity} size="xs" aria-hidden />,
            })),
        [entities],
    );

    const onValueChange = useCallback(
        (id: number | null) => {
            onChange(id === null ? undefined : entitiesById.get(id));
        },
        [entitiesById, onChange],
    );

    return (
        <div className={className}>
            <Picker.Select
                items={items}
                value={selectedEntity?.id ?? null}
                onValueChange={onValueChange}
                disabled={disabled}
                placeholder={placeholder}
                renderValue={() =>
                    selectedEntity ? (
                        <EntityDisplay entity={selectedEntity} />
                    ) : undefined
                }
            />
        </div>
    );
};
